import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { IataCode } from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { getDropdownOptions } from '@/modules/dataParser.js';

function buildAbstractList(data: IataCode[]): string[] {
  return [
    'N/A',
    ...data
      .map(entry => entry.iata ?? '')
      .filter((iata): iata is string => Boolean(iata))
      .sort((left, right) => (left > right ? 1 : -1)),
  ];
}

export const useIataStore = defineStore('iata', () => {
  const apiClient = useApiClient();

  const list = ref<IataCode[] | null>(null);
  const abstract = ref<string[] | null>(null);
  const loading = ref<boolean>(true);

  const options = computed(() => getDropdownOptions(abstract.value));

  async function get(): Promise<IataCode[]> {
    if (list.value) {
      return list.value;
    }

    loading.value = true;

    try {
      const response = await apiClient.iata.get();
      list.value = response ?? [];
      abstract.value = buildAbstractList(list.value);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  async function getAbstract(): Promise<string[]> {
    if (abstract.value) {
      return abstract.value;
    }

    await get();
    return abstract.value ?? ['N/A'];
  }

  return {
    list,
    abstract,
    loading,
    options,
    get,
    getAbstract,
  };
});
